import { useState } from 'react';
import {
  AppButton,
  AppChip,
  AppDivider,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppReorderable,
  AppScreenScaffold,
  AppSwipeRow,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  Icon,
  IconButton,
} from '@core/designsystem';
import {
  BatchDialog,
  NameDocumentDialog,
  PagedCameraDialog,
  SourcesSheet,
  suggestionFor,
  type AddSource,
} from './AddDocument';
import { DocumentRow } from './DocumentRow';
import { ScanEditor } from './ScanEditor';
import { DOCUMENT_TYPES, documentsCount, typeLabel } from './formatter';
import { SORTS, visibleDocuments, type DocumentListItem, type DocumentsState } from './types';
import { processShot, type Shot } from './scanning';
import type { DocumentsActions } from './useDocuments';
import './documents.css';

/**
 * Библиотека документов дела.
 *
 * Чистая функция состояния: ни базы, ни навигации внутри — её знает `DocumentsRoute`.
 * Списки блоками не оформляются: документы однородны, и блоки разрезали бы прокрутку
 * (CLAUDE.md, правило 3).
 */
export function DocumentsScreen({
  state,
  actions,
  onBack,
  onOpenDocument,
}: {
  state: DocumentsState;
  actions: DocumentsActions;
  onBack: () => void;
  /** Просмотр документа появится на шаге 12; до него строка открывает меню. */
  onOpenDocument?: (id: string) => void;
}) {
  const [menuFor, setMenuFor] = useState<DocumentListItem | null>(null);
  const [renameFor, setRenameFor] = useState<DocumentListItem | null>(null);
  const [typeFor, setTypeFor] = useState<string[] | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  /** Что выбрано и ждёт названия с типом. Пусто — ничего не добавляем. */
  const [pending, setPending] = useState<{
    files: File[];
    photos: boolean;
    fromCamera: boolean;
  } | null>(null);

  /** Страницы, снятые подряд системной камерой: замена сканера там, где его нет. */
  const [cameraPages, setCameraPages] = useState<File[] | null>(null);

  /** Снятый кадр, у которого правят границы листа. */
  const [editing, setEditing] = useState<Shot | null>(null);
  const [flattening, setFlattening] = useState(false);

  const takePhotos = (photos: File[], source: AddSource) => {
    if (photos.length === 0) return;

    // Снятое камерой проходит обработку: поиск листа, выпрямление, чистка света —
    // то, что на Android делает сканер ML Kit. Выбранное в галерее не трогаем:
    // там может быть что угодно, включая уже готовый скан.
    if (source === 'camera') {
      const shot = photos[0];
      if (!shot) return;

      void processShot(shot).then(setEditing);
      return;
    }
    setPending({ files: photos, photos: true, fromCamera: false });
  };

  /** Съёмка закончена: один кадр — обычный документ, несколько — том дела. */
  const finishCamera = () => {
    const pages = cameraPages ?? [];
    setCameraPages(null);
    if (pages.length > 0) setPending({ files: pages, photos: true, fromCamera: true });
  };

  const addPending = (type: (typeof DOCUMENT_TYPES)[number], title: string | null, merge: boolean) => {
    if (!pending) return;

    if (!pending.photos) void actions.addFiles(pending.files, type, title);
    else if (merge) void actions.addPhotosAsOne(pending.files, type, title, pending.fromCamera);
    else void actions.addPhotos(pending.files, type, title, pending.fromCamera);

    setPending(null);
  };

  const documents = visibleDocuments(state.entries);
  const selecting = state.selectedIds.length > 0;
  const sortLabel = SORTS.find((item) => item.key === state.sort)?.label ?? '';

  const openMenu = (item: DocumentListItem) => {
    // Содержимое читается заранее: «Поделиться» обязано открыть лист без ожидания.
    actions.prepare([item.id]);
    setMenuFor(item);
  };

  const rowOf = (item: DocumentListItem, handle?: React.ReactNode) => (
    <DocumentRow
      item={item}
      selected={state.selectedIds.includes(item.id)}
      selectable={selecting}
      onOpen={() => (selecting ? actions.toggleSelection(item.id) : onOpenDocument?.(item.id))}
      onToggleFavorite={() => void actions.toggleFavorite(item.id, !item.isFavorite)}
      onMenu={() => (selecting ? actions.toggleSelection(item.id) : openMenu(item))}
      handle={handle}
    />
  );

  if (editing) {
    return (
      <ScanEditor
        photo={editing.photo}
        corners={editing.corners}
        detected={editing.detected}
        busy={flattening}
        onCancel={() => setEditing(null)}
        onConfirm={(corners) => {
          setFlattening(true);
          void editing
            .flatten(corners)
            .then((page) => {
              setCameraPages((pages) => [...(pages ?? []), page]);
              setEditing(null);
            })
            .finally(() => setFlattening(false));
        }}
      />
    );
  }

  return (
    <AppScreenScaffold
      topBar={
        selecting ? (
          <AppTopBar
            contextual
            title={`Выбрано: ${state.selectedIds.length}`}
            leading={
              <IconButton icon="close" label="Снять выделение" onClick={actions.clearSelection} />
            }
            actions={
              <>
                <IconButton
                  icon="share"
                  label="Поделиться"
                  onClick={() => actions.share(state.selectedIds, state.caseTitle)}
                />
                {state.selectedIds.length > 1 ? (
                  <IconButton
                    icon="description"
                    label="Объединить"
                    onClick={() => setMerging(true)}
                  />
                ) : null}
                <IconButton
                  icon="label"
                  label="Тип документа"
                  onClick={() => setTypeFor(state.selectedIds)}
                />
                <IconButton
                  icon="delete"
                  label="Удалить"
                  onClick={() => void actions.remove(state.selectedIds)}
                />
              </>
            }
          />
        ) : (
          <AppTopBar
            title="Документы"
            subtitle={state.caseTitle}
            onBack={onBack}
            actions={
              <IconButton icon="tune" label="Сортировка" onClick={() => setSortOpen(true)} />
            }
          />
        )
      }
      overlay={
        <>
          {sortOpen ? (
            <AppMenuSheet
              title="Порядок документов"
              onDismiss={() => setSortOpen(false)}
              items={SORTS.map((item) => ({
                icon: item.key === state.sort ? ('check' as const) : ('swap_vert' as const),
                label: item.label,
                onSelect: () => actions.setSort(item.key),
              }))}
            />
          ) : null}

          {menuFor ? (
            <AppMenuSheet
              title={menuFor.title}
              onDismiss={() => setMenuFor(null)}
              items={[
                {
                  icon: 'star',
                  label: menuFor.isFavorite ? 'Убрать из избранного' : 'В избранное',
                  onSelect: () => void actions.toggleFavorite(menuFor.id, !menuFor.isFavorite),
                },
                {
                  icon: 'edit',
                  label: 'Переименовать',
                  onSelect: () => setRenameFor(menuFor),
                },
                {
                  icon: 'label',
                  label: 'Тип документа',
                  onSelect: () => setTypeFor([menuFor.id]),
                },
                {
                  icon: 'share',
                  label: 'Поделиться',
                  onSelect: () => actions.share([menuFor.id], menuFor.title),
                },
                {
                  icon: 'checklist',
                  label: 'Выбрать несколько',
                  onSelect: () => actions.toggleSelection(menuFor.id),
                },
                {
                  icon: 'delete',
                  label: 'Удалить',
                  danger: true,
                  onSelect: () => void actions.remove([menuFor.id]),
                },
              ]}
            />
          ) : null}

          {renameFor ? (
            <RenameDialog
              item={renameFor}
              onDismiss={() => setRenameFor(null)}
              onConfirm={(title) => {
                void actions.rename(renameFor.id, title);
                setRenameFor(null);
              }}
            />
          ) : null}

          {typeFor ? (
            <AppMenuSheet
              title="Тип документа"
              onDismiss={() => setTypeFor(null)}
              items={DOCUMENT_TYPES.map((type) => ({
                icon: 'label' as const,
                label: typeLabel(type),
                onSelect: () => void actions.setType(typeFor, type),
              }))}
            />
          ) : null}

          {sourcesOpen ? (
            <SourcesSheet
              onDismiss={() => setSourcesOpen(false)}
              onPhotos={takePhotos}
              onFiles={(chosen) =>
                setPending({ files: chosen, photos: false, fromCamera: false })
              }
            />
          ) : null}

          {cameraPages && cameraPages.length > 0 ? (
            <PagedCameraDialog
              pages={cameraPages.length}
              onShootMore={(photos) => takePhotos(photos, 'camera')}
              onFinish={finishCamera}
            />
          ) : null}

          {pending && pending.files.length === 1 ? (
            <NameDocumentDialog
              suggestion={pending.photos ? '' : suggestionFor(pending.files[0])}
              onDismiss={() => setPending(null)}
              onConfirm={(title, type) => addPending(type, title, false)}
            />
          ) : null}

          {pending && pending.files.length > 1 ? (
            <BatchDialog
              count={pending.files.length}
              canMerge={pending.photos}
              onDismiss={() => setPending(null)}
              onConfirm={(choice) => addPending(choice.type, choice.title, choice.mergeIntoOne)}
            />
          ) : null}

          {merging ? (
            <MergeDialog
              count={state.selectedIds.length}
              // Текстовый слой есть только у принесённого файла: у скана и снимка
              // терять нечего, и пугать ими незачем ([D-41]).
              warnAboutText={documents.some(
                (item) => state.selectedIds.includes(item.id) && item.source === 'Imported',
              )}
              onDismiss={() => setMerging(false)}
              onConfirm={(title) => {
                void actions.merge(state.selectedIds, title);
                setMerging(false);
              }}
            />
          ) : null}

          {state.pendingDeletion ? (
            <AppUndoSnackbar
              text={state.pendingDeletion.message}
              onUndo={() => void actions.undoDelete()}
              onExpire={actions.deletionShown}
            />
          ) : null}

          {state.errorMessage ? (
            <AppUndoSnackbar
              text={state.errorMessage}
              actionLabel="Понятно"
              onUndo={actions.clearError}
              onExpire={actions.clearError}
            />
          ) : null}
        </>
      }
    >
      <div className="documents__filters">
        <AppChip
          label="Все"
          selected={state.filter.kind === 'all'}
          onClick={() => actions.setFilter({ kind: 'all' })}
        />
        {state.hasFavorites ? (
          <AppChip
            label="Избранные"
            selected={state.filter.kind === 'favorites'}
            onClick={() => actions.setFilter({ kind: 'favorites' })}
          />
        ) : null}
        {state.availableTypes.map((type) => (
          <AppChip
            key={type}
            label={typeLabel(type)}
            selected={state.filter.kind === 'type' && state.filter.type === type}
            onClick={() => actions.setFilter({ kind: 'type', type })}
          />
        ))}
      </div>

      <div className="documents__toolbar">
        <span className="documents__summary">{state.summary}</span>
        <button type="button" className="documents__sort" onClick={() => setSortOpen(true)}>
          <Icon name="swap_vert" size={18} />
          {sortLabel}
        </button>
      </div>

      <AppDivider />

      {state.isReordering ? (
        <div className="documents__reorder-hint">
          <span>Перетащите документы в нужном порядке</span>
          <AppButton variant="text" onClick={actions.finishReordering}>
            Готово
          </AppButton>
        </div>
      ) : null}

      {documents.length === 0 && !state.isLoading ? (
        <AppEmptyState
          icon="picture_as_pdf"
          text={state.filter.kind === 'all' ? 'Документов пока нет' : 'В этом отборе пусто'}
          action={
            state.filter.kind === 'all' ? (
              <AppButton onClick={() => setSourcesOpen(true)}>Добавить документ</AppButton>
            ) : null
          }
        />
      ) : null}

      <div className="documents__list">
        {state.isReordering ? (
          <AppReorderable
            items={documents}
            keyOf={(item) => item.id}
            onReorder={(ids) => void actions.reorder(ids)}
            renderRow={(item, handle) => rowOf(item, handle)}
          />
        ) : (
          state.entries.map((entry) =>
            entry.kind === 'header' ? (
              <h2 key={`header-${entry.title}`} className="documents__header">
                {entry.title}
                <span className="documents__header-count">{entry.count}</span>
              </h2>
            ) : (
              <div key={entry.document.id}>
                <AppSwipeRow
                  start={{
                    icon: 'star',
                    label: entry.document.isFavorite ? 'Убрать из избранного' : 'В избранное',
                    onAction: () =>
                      void actions.toggleFavorite(entry.document.id, !entry.document.isFavorite),
                  }}
                  onLongPress={() => actions.toggleSelection(entry.document.id)}
                >
                  {rowOf(entry.document)}
                </AppSwipeRow>
                <AppDivider withIcon />
              </div>
            ),
          )
        )}
      </div>

      {documents.length > 0 ? (
        <div className="documents__add">
          <AppButton variant="text" onClick={() => setSourcesOpen(true)}>
            Добавить документ
          </AppButton>
          {state.isImporting ? <span className="documents__importing">Добавляем…</span> : null}
        </div>
      ) : null}
    </AppScreenScaffold>
  );
}

/**
 * Переименование документа.
 *
 * Отдельный диалог, а не правка на месте: строка списка узкая, а имя документа
 * юрист правит редко и целиком.
 */
function RenameDialog({
  item,
  onDismiss,
  onConfirm,
}: {
  item: DocumentListItem;
  onDismiss: () => void;
  onConfirm: (title: string) => void;
}) {
  const [title, setTitle] = useState(item.title);

  return (
    <AppFormDialog
      title="Переименовать"
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: () => onConfirm(title), enabled: title.trim().length > 0 }}
    >
      <AppTextField value={title} onChange={setTitle} label="Название" autoFocus />
    </AppFormDialog>
  );
}

/**
 * Объединение документов.
 *
 * Склейка растровая: текстовый слой у принесённого PDF после неё пропадёт, и сказать
 * об этом надо **до** нажатия, а не после ([D-41](docs/DECISIONS.md)). У сканов и снимков
 * текста нет изначально — там предупреждать не о чем, и лишних слов не показываем.
 */
function MergeDialog({
  count,
  warnAboutText,
  onDismiss,
  onConfirm,
}: {
  count: number;
  warnAboutText: boolean;
  onDismiss: () => void;
  onConfirm: (title: string) => void;
}) {
  const [title, setTitle] = useState('');

  return (
    <AppFormDialog
      title={`Объединить ${documentsCount(count)}`}
      onDismiss={onDismiss}
      confirm={{ label: 'Объединить', onClick: () => onConfirm(title) }}
    >
      <AppTextField
        value={title}
        onChange={setTitle}
        label="Название, например «Материалы дела»"
        autoFocus
      />
      <p className="add-document__hint">
        Исходные документы спрячутся, но не пропадут: «Отменить» вернёт их.
      </p>
      {warnAboutText ? (
        <p className="add-document__hint">
          Среди выбранных есть принесённый файл. После объединения поиск по тексту внутри
          него работать не будет — страницы станут картинками.
        </p>
      ) : null}
    </AppFormDialog>
  );
}
